import { Injectable } from '@angular/core';
import { AppState } from '../app.state';

import { Role, RoleHandler } from '../model.module/constant/rights.enum';
import { KundenHandle } from '../model.module/xhr/kunden-handle.model';
import { AuthKeycloakService } from './auth-keycloak.service';

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    private userId: string;

    private loggedIn = false;

    private role: Role = null;

    private roleName = null;

    private userRealName: string = null;

    private readonly onUpdateFns: Function[] = [];
    private readonly onKundenUpdateFns: Function[] = [];
    private readonly onLoginFns: Function[] = [];

    private activeKunde: KundenHandle = null;

    private activeKundeOnLogin: KundenHandle = null;

    constructor(
        private readonly authKeycloakService: AuthKeycloakService,
        private readonly state: AppState,
    ) {
        this.initialize();
    }

    addOnKundenUpdateFn(fn: () => void) {
        this.onKundenUpdateFns.push(fn);
    }

    isMaklerOrVermittler(): boolean {
        return RoleHandler.isMaklerOrStbrRole(this.role);
    }

    isSteuerberater(): boolean {
        return RoleHandler.isSteuerberaterRole(this.role);
    }

    addOnUpdateFn(fn: () => {}) {
        this.onUpdateFns.push(fn);
    }

    addOnLoginFn(fn: () => {}) {
        this.onLoginFns.push(fn);
    }

    getActiveKunde = (): KundenHandle => {
        return this.activeKunde;
    };

    getActiveKundeOnLogin = (): KundenHandle => {
        return this.activeKundeOnLogin;
    };

    setActiveKunde = (kunde: KundenHandle) => {
        this.activeKunde = kunde;
        this.onKundenUpdate();
    };

    /**
     * Resets the active Kunde back to login state.
     */
    resetActiveKunde()  {
        this.activeKunde = KundenHandle.copy(this.activeKundeOnLogin);
        this.role = RoleHandler.getRole(this.roleName);
    };

    isActiveKunde4Kunde = () => {
        return this.activeKunde.vsnr !== this.activeKundeOnLogin.vsnr;
    };

    getRootUserId(): string {
        return this.determineKundennummer(this.userId);
    }

    getUserId(): string {
        return this.userId;
    }

    getUserRealName(): string {
        return this.userRealName;
    }

    isLoggedIn(): boolean {
        return this.loggedIn;
    }

    getRole(): Role {
        return this.role;
    }

    getRoleName(): string {
        return this.roleName;
    }

    setForKunde(): void {
        this.role = RoleHandler.qualifyUser(this.roleName);
    }

    logout(): void {
        this.loggedIn = false;
        this.authKeycloakService.logout();
    }

    determineKundennummer(userName: string): string {
        return userName.substring(0, userName.length - 4) + '0000';
    }

    private initialize(): void {
        this.authKeycloakService.userProfile$.subscribe(userProfile => {
            this.loggedIn = userProfile != null;
            if (this.loggedIn) {
                this.updateUser(userProfile);
            } else {
                this.activeKunde = null;
            }
        });
    }

    private onKundenUpdate() {
        this.onKundenUpdateFns.forEach(f => f());
    }

    private updateUser(userProfileInfo) {
        const userProfile = userProfileInfo.info;
        const roleHandle = RoleHandler.handleFromLdapIdentifier(userProfile.employeetype);
        const isNoMaklerOderStbr = !RoleHandler.isMaklerOrStbr(roleHandle);

        const kundenHandle: KundenHandle = KundenHandle.fromData(
            this.determineKundennummer(userProfile.uid),
            userProfile.fullname,
            isNoMaklerOderStbr,
            isNoMaklerOderStbr,
        );

        this.setActiveKunde(kundenHandle);
        this.activeKundeOnLogin = kundenHandle;

        this.userId = userProfile.uid;
        this.userRealName = userProfile.fullname;
        this.loggedIn = true;
        this.roleName = roleHandle;
        this.role = RoleHandler.getRole(this.roleName);

        this.state.saveToken({
            userName: this.userId,
            role: this.roleName,
            timestamp: new Date().getTime(),
            app: 'kreditportal'
        });
    }

}
